import type { LucideIcon } from 'lucide-react';
import { CheckCircle2, Clock3, FileText, ShieldAlert } from 'lucide-react';

/**
 * Live billing state of an IPD visit, as resolved by the backend
 * (`attachBillingSummaries`):
 *  - `DUE`        an invoice still carries an unpaid balance
 *  - `PARTIAL`    invoices settled, but charges are not invoiced yet
 *  - `PAID`       fully invoiced and fully settled
 *  - `RISK_FUND`  the outstanding balance was written off to the Risk Fund
 *  - `NO_INVOICE` nothing has been billed for the visit yet
 */
export type AdmissionPaymentState =
  | 'PAID'
  | 'DUE'
  | 'PARTIAL'
  | 'RISK_FUND'
  | 'NO_INVOICE';

export interface AdmissionPaymentMeta {
  label: string;
  icon: LucideIcon;
  /** Compact chip used in the admissions list. */
  chip: string;
  /** Stronger tint used on the detail page and the details modal. */
  highlight: string;
  /** Tooltip copy explaining what the state means. */
  description: string;
}

export const ADMISSION_PAYMENT_META: Record<AdmissionPaymentState, AdmissionPaymentMeta> = {
  PAID: {
    label: 'Paid',
    icon: CheckCircle2,
    chip: 'bg-emerald-100 text-emerald-700',
    highlight: 'bg-success/10 text-success ring-1 ring-success/30',
    description: 'Every charge is invoiced and the bill is fully settled.',
  },
  DUE: {
    label: 'Due',
    icon: Clock3,
    chip: 'bg-rose-100 text-rose-700',
    highlight: 'bg-danger/10 text-danger ring-1 ring-danger/30',
    description: 'The bill is invoiced but a balance is still unpaid.',
  },
  PARTIAL: {
    label: 'Partial',
    icon: FileText,
    chip: 'bg-amber-100 text-amber-700',
    highlight: 'bg-warning/10 text-warning ring-1 ring-warning/30',
    description:
      'Invoices raised so far are settled, but some charges are not invoiced yet.',
  },
  RISK_FUND: {
    label: 'Risk Fund',
    icon: ShieldAlert,
    // Deliberately the loudest treatment of the set: a written-off balance is
    // not money the patient still owes, so it must never be mistaken for
    // "Paid" or "Due" on a detail screen.
    chip: 'bg-warning text-white ring-2 ring-warning/40 shadow-sm',
    highlight: 'bg-warning text-white ring-2 ring-warning/40 shadow-md shadow-warning/20',
    description:
      'The outstanding balance was written off and moved to the Risk Fund — nothing is left to collect from the patient.',
  },
  NO_INVOICE: {
    label: 'Not billed',
    icon: FileText,
    chip: 'bg-slate-100 text-slate-600',
    highlight: 'bg-slate-500/10 text-slate-500 ring-1 ring-slate-300/70',
    description: 'No invoice has been raised for this visit yet.',
  },
};

const PAYMENT_STATES = Object.keys(ADMISSION_PAYMENT_META) as AdmissionPaymentState[];

/**
 * Invoice/diagnostic statuses are stored lowercase (`risk_fund`), while the
 * derived admission payment state is uppercase (`RISK_FUND`). Compare the
 * document status case-insensitively so a written-off invoice is never read as
 * merely settled.
 */
export function isRiskFundInvoiceStatus(status?: string | null): boolean {
  return String(status ?? '').toLowerCase() === 'risk_fund';
}

/** Turns a raw backend string into a known state, or `null` when unknown. */
export function normalizeAdmissionPaymentState(
  value?: string | null,
): AdmissionPaymentState | null {
  if (!value) return null;
  const upper = String(value).toUpperCase();
  return PAYMENT_STATES.includes(upper as AdmissionPaymentState)
    ? (upper as AdmissionPaymentState)
    : null;
}

/**
 * Falls back to deriving the state from an admission row when the backend did
 * not include `paymentStatus` (older cached rows, for example).
 */
export function resolveAdmissionPaymentState(
  admission: {
    paymentStatus?: string | null;
    invoiceCount?: number | null;
    dueAmount?: number | null;
    unbilledCount?: number | null;
  } | null
  | undefined,
): AdmissionPaymentState {
  const explicit = normalizeAdmissionPaymentState(admission?.paymentStatus);
  if (explicit) return explicit;

  const invoiceCount = Number(admission?.invoiceCount || 0);
  const dueAmount = Number(admission?.dueAmount || 0);
  const unbilledCount = Number(admission?.unbilledCount || 0);

  if (invoiceCount === 0) return 'NO_INVOICE';
  if (dueAmount > 0) return 'DUE';
  if (unbilledCount > 0) return 'PARTIAL';
  return 'PAID';
}

/**
 * Derives the state from the live billing-status payload. Returns `null` when
 * no invoice exists yet, so callers can fall back to the admission row.
 *
 * This is the only reliable way to spot a Risk Fund write-off on a detail
 * screen: clearing the due makes the old `due > 0 ? Due : Paid` check report a
 * written-off bill as paid.
 */
export function paymentStateFromBilling(input: {
  invoices?: Array<{ status?: string | null; dueAmount?: number | null }> | null;
  outstandingDue?: number | null;
  unbilledCount?: number | null;
  /**
   * Ignore unbilled charges when deciding between Partial / Risk Fund / Paid.
   * A cancelled stay is void: its leftover unbilled charges can never become an
   * invoice, so they must not hide the Risk Fund write-off made on cancellation.
   */
  ignoreUnbilled?: boolean;
}): AdmissionPaymentState | null {
  const invoices = Array.isArray(input.invoices) ? input.invoices : [];
  if (invoices.length === 0) return null;

  const due =
    input.outstandingDue === undefined || input.outstandingDue === null
      ? invoices.reduce((sum, inv) => sum + (Number(inv.dueAmount) || 0), 0)
      : Number(input.outstandingDue) || 0;

  if (due > 0.004) return 'DUE';
  if (!input.ignoreUnbilled && Number(input.unbilledCount || 0) > 0) return 'PARTIAL';
  if (invoices.some((inv) => isRiskFundInvoiceStatus(inv.status))) return 'RISK_FUND';
  return 'PAID';
}
